import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ISSUES = [
  {
    id: "housing",
    title: "Housing Affordability & Negative Gearing",
    color: "#CC2529",
    summary: "Australia's housing crisis has seen median home prices exceed $1M in major cities. Negative gearing (offsetting rental losses against income) and the capital gains tax discount are central to the debate.",
    tags: ["Property", "Tax", "First Home Buyers"],
  },
  {
    id: "climate",
    title: "Climate Change & Net Zero",
    color: "#10B981",
    summary: "Australia is one of the world's highest per-capita carbon emitters. The debate centres on net zero targets, coal exports, renewable energy investment, and nuclear power alternatives.",
    tags: ["Environment", "Energy", "Economy"],
  },
  {
    id: "medicare",
    title: "Medicare & Public Healthcare",
    color: "#2E5948",
    summary: "Medicare provides universal healthcare but faces strain from underfunding, GP shortages, and bulk-billing rates declining below 80%. Mental health services and hospital waiting times are flashpoints.",
    tags: ["Health", "Funding", "Bulk Billing"],
  },
  {
    id: "hecs",
    title: "HECS-HELP & University Education",
    color: "#7C3AED",
    summary: "Australia's student loan system (HECS-HELP) has seen debt grow significantly due to indexation linked to CPI. Many graduates carry debts exceeding $50,000. Free uni advocates vs cost-recovery debates continue.",
    tags: ["Education", "Students", "Debt"],
  },
  {
    id: "nuclear",
    title: "Nuclear Energy Debate",
    color: "#D97706",
    summary: "The Coalition proposed building nuclear power plants by 2035–2040. Proponents cite stable baseload power; opponents cite cost blowouts, timelines, and Australia's lack of nuclear infrastructure.",
    tags: ["Energy", "Policy", "Future"],
  },
  {
    id: "immigration",
    title: "Immigration & Australia's Population",
    color: "#1B4F9E",
    summary: "Australia's net overseas migration reached record levels post-COVID, driving housing and infrastructure pressure. Debates focus on the right intake level, skills vs family visas, and asylum seeker policy.",
    tags: ["Population", "Borders", "Economy"],
  },
  {
    id: "cost",
    title: "Cost of Living Crisis",
    color: "#C04E3F",
    summary: "Inflation hit 8% peak in 2022–23 and interest rate rises pushed mortgage repayments to historic highs. Groceries, energy bills, and rent are the everyday flashpoints affecting household budgets.",
    tags: ["Inflation", "Interest Rates", "Wages"],
  },
  {
    id: "indigenous",
    title: "Indigenous Rights & the Voice",
    color: "#065F46",
    summary: "The failed 2023 Voice to Parliament referendum left Indigenous policy at a crossroads. Issues include treaty negotiations, the Closing the Gap framework, land rights, and constitutional recognition.",
    tags: ["First Nations", "Rights", "Constitution"],
  },
];

import DOMPurify from "dompurify";

function renderMd(text) {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\n\n/g, "</p><p>")
    .replace(/\n/g, "<br/>");
}

function IssueCard({ issue, isDark }) {
  const [expanded, setExpanded] = useState(false);
  const [aiContent, setAiContent] = useState("");
  const [loading, setLoading] = useState(false);

  const surface = isDark ? "#1E293B" : "#FFFFFF";
  const text = isDark ? "#F8FAFC" : "#0F172A";
  const textSec = isDark ? "#94A3B8" : "#475569";

  const fetchExplainer = async () => {
    if (aiContent) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/ai/issue-explainer`, {
        issue_id: issue.id,
        issue_title: issue.title,
        issue_summary: issue.summary,
      });
      setAiContent(res.data.explainer);
    } catch {
      setAiContent("Failed to load explainer. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="border-2 border-black overflow-hidden transition-all"
      style={{ backgroundColor: surface, boxShadow: "4px 4px 0px 0px #000" }}
      data-testid={`issue-card-${issue.id}`}
    >
      <div className="h-1.5 w-full" style={{ backgroundColor: issue.color }} />
      <div className="p-5">
        <h3
          className="font-black text-base mb-2"
          style={{ fontFamily: "Outfit, sans-serif", color: text }}
        >
          {issue.title}
        </h3>
        <p className="text-sm mb-3 leading-relaxed" style={{ color: textSec }}>
          {issue.summary}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {issue.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 border border-black font-medium"
              style={{ backgroundColor: `${issue.color}18`, color: issue.color }}
            >
              {tag}
            </span>
          ))}
        </div>
        <button
          data-testid={`explain-issue-${issue.id}`}
          onClick={() => { setExpanded(!expanded); if (!expanded) fetchExplainer(); }}
          className="flex items-center gap-2 text-sm font-bold border-2 border-black px-4 py-2 hover:-translate-y-0.5 transition-transform"
          style={{ backgroundColor: issue.color, color: "#fff", boxShadow: "2px 2px 0px 0px #000" }}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? "Hide" : "AI Deep Dive"}
        </button>

        {expanded && (
          <div className="mt-4 pt-4 border-t-2 border-black">
            {loading ? (
              <div className="flex items-center gap-2 py-4">
                <Loader2 className="w-5 h-5 animate-spin" style={{ color: issue.color }} />
                <span className="text-sm" style={{ color: textSec }}>Generating deep dive...</span>
              </div>
            ) : (
              <div
                className="text-sm leading-relaxed"
                style={{ color: text, fontFamily: "Public Sans, sans-serif" }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<p>${renderMd(aiContent)}</p>`) }}
                data-testid={`issue-explainer-${issue.id}`}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function IssuesPage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const bg = isDark ? "#0F172A" : "#F9F7F2";
  const text = isDark ? "#F8FAFC" : "#0F172A";

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg }} data-testid="issues-page">
      <div className="py-12 px-4 border-b-2 border-black" style={{ backgroundColor: "#0F172A" }}>
        <div className="max-w-5xl mx-auto">
          <button
            data-testid="issues-back-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-bold mb-5"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <div
            className="inline-block px-3 py-1 mb-3 text-xs font-bold tracking-widest border-2 uppercase"
            style={{ borderColor: "#FFC82E", color: "#FFC82E" }}
          >
            Issue Deep Dives
          </div>
          <h1
            className="text-4xl sm:text-5xl font-black text-white mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            The Issues That Matter
          </h1>
          <p className="text-base" style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Public Sans, sans-serif" }}>
            Clear, unbiased explainers on the key political issues in Australia. Click "AI Deep Dive" on any issue for a comprehensive analysis.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {ISSUES.map((issue) => (
            <IssueCard key={issue.id} issue={issue} isDark={isDark} />
          ))}
        </div>
        <p className="text-sm mt-8 text-center" style={{ color: isDark ? "#94A3B8" : "#475569" }}>
          AI explainers are generated by GPT-4o for civic education purposes. Content may not reflect the latest developments.
        </p>
      </div>
    </div>
  );
}
